import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { X, Send, MessagesSquare, RefreshCw, ChevronLeft, Inbox, Siren, Check, ArrowRight } from "lucide-react";
import api from "../../api";
import Portal from "../common/Portal";
import { subscribePulse, refreshPulse } from "../../utils/pulse";

// Every message with a patient, across their sessions (GET /patients/<id>/chat).
// Left: sessions, to read one session's messages on its own. Right: the chat,
// with the patient's own Get help (SOS) requests shown in red where they
// happened (read only, from crisis_alerts). A new message always goes on the
// current/last session, whichever session is being read (the server decides,
// messaging/patient_chat.py).
const fmtDay = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");
const STATE = {
  today: { label: "Today", style: "bg-emerald-100 text-emerald-800" },
  upcoming: { label: "Upcoming", style: "bg-sky-100 text-sky-800" },
  not_accepted: { label: "Not accepted", style: "bg-amber-100 text-amber-800" },
  cancelled: { label: "Cancelled", style: "bg-secondary-100 text-secondary-500" },
};
const sessionTitle = (s) => `${s.session_number ? `Session ${s.session_number}` : "Visit"} · ${fmtDay(s.date)}`;

function SosCard({ a, onOpenRecord }) {
  const open = a.status === "open";
  return (
    <div className="my-2 rounded-xl border-2 border-red-500 bg-red-50 px-4 py-3 shadow-sm">
      <p className="flex flex-wrap items-center gap-2 text-sm font-extrabold text-red-700">
        <Siren className="w-5 h-5" /> PATIENT ASKED FOR HELP (SOS)
        <span className="font-semibold text-red-600">· {fmtTime(a.created_at)}</span>
      </p>
      <p className={`mt-1 text-sm ${open ? "font-bold text-red-700" : "text-secondary-700"}`}>
        {open ? "No one has responded yet."
          : a.status === "acknowledged" ? `${a.acknowledged_by || "A colleague"} is handling it (since ${fmtTime(a.acknowledged_at)}).`
          : `Closed by ${a.resolved_by || "a colleague"} on ${fmtTime(a.resolved_at)}.`}
      </p>
      {a.status === "resolved" && a.resolution_note && <p className="mt-1 text-sm text-secondary-700 whitespace-pre-wrap">Note: {a.resolution_note}</p>}
      {a.status !== "resolved" && (
        <button type="button" onClick={onOpenRecord} className="mt-2 inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700">
          Open the patient's record to respond <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export default function PatientChatModal({ patientId, patientName, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // appointment_id, null = all
  const [showList, setShowList] = useState(true); // phones: list or chat
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const crisisSig = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.getPatientChat(patientId)
      .then((r) => {
        setData(r.data); setError("");
        // Opening marked the patient's messages read: update the bell.
        if ((r.data?.sessions || []).some((s) => s.unread)) refreshPulse();
      })
      .catch((e) => setError(e?.response?.data?.error || "Couldn't load the messages."));
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  // New messages and SOS changes arrive through the shared /pulse poll, not a poll of our own.
  const apptIds = useMemo(() => new Set((data?.sessions || []).map((s) => s.appointment_id)), [data]);
  useEffect(() => subscribePulse((p) => {
    const sig = (Array.isArray(p?.crisis) ? p.crisis : []).filter((a) => a.patient_id === patientId).map((a) => `${a.id}:${a.status}`).join(",");
    const crisisChanged = crisisSig.current !== null && sig !== crisisSig.current;
    crisisSig.current = sig;
    if (crisisChanged || (p?.messages?.threads || []).some((t) => apptIds.has(t.appointment_id))) load();
  }), [apptIds, load, patientId]);

  const sessions = data?.sessions || [];
  const byAppt = useMemo(() => Object.fromEntries(sessions.map((s) => [s.appointment_id, s])), [sessions]);
  const current = data?.current_appointment_id ? byAppt[data.current_appointment_id] : null;
  const inView = (x) => !selected || x.appointment_id === selected;
  const timeline = [
    ...(data?.messages || []).filter(inView).map((m) => ({ kind: "msg", at: m.created_at, appointment_id: m.appointment_id, m })),
    ...(data?.sos || []).filter(inView).map((a) => ({ kind: "sos", at: a.created_at, appointment_id: a.appointment_id, a })),
  ].sort((x, y) => (x.at || "").localeCompare(y.at || ""));
  const openSos = (data?.sos || []).filter((a) => a.status === "open");

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [timeline.length, selected]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.sendPatientChat(patientId, { message: body });
      setDraft("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't send the message.");
    } finally {
      setSending(false);
    }
  };

  const pick = (id) => { setSelected(id); setShowList(false); };
  const openRecord = () => { onClose(); navigate(`/patient/${patientId}`); };
  const total = data?.messages?.length || 0;
  const rowClass = (on) => `w-full text-left rounded-xl border-2 px-3 py-2.5 transition-colors ${on
    ? "border-primary-500 bg-primary-50 shadow-sm"
    : "border-transparent bg-white hover:border-primary-200 hover:bg-primary-50/40"}`;

  return (
    <Portal>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 sm:p-4">
        <div className="flex h-full w-full max-w-5xl flex-col bg-white shadow-xl sm:h-[88vh] sm:rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 border-b border-secondary-100 px-4 py-3">
            <MessagesSquare className="w-5 h-5 text-primary-600" />
            <h3 className="flex-1 min-w-0 truncate font-bold text-secondary-900">Messages with {data?.patient?.name || patientName}</h3>
            <button type="button" onClick={load} aria-label="Refresh" className="p-1.5 rounded-full text-secondary-400 hover:text-secondary-700 hover:bg-secondary-50"><RefreshCw className="w-4 h-4" /></button>
            <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-full text-secondary-400 hover:text-secondary-700 hover:bg-secondary-50"><X className="w-5 h-5" /></button>
          </div>

          {openSos.length > 0 && (
            <button type="button" onClick={openRecord}
              className="flex items-center gap-2 bg-red-600 px-4 py-2.5 text-left text-sm font-extrabold text-white hover:bg-red-700">
              <Siren className="w-5 h-5 shrink-0 animate-pulse" />
              <span className="flex-1">SOS: the patient asked for help on {fmtTime(openSos[openSos.length - 1].created_at)} and no one has responded yet.</span>
              <span className="shrink-0 underline">Respond</span>
            </button>
          )}

          {error ? <p className="p-6 text-sm text-red-600">{error}</p> : !data ? <p className="p-6 text-sm text-secondary-500">Loading…</p> : (
            <div className="flex flex-1 min-h-0">
              {/* Sessions */}
              <aside className={`${showList ? "flex" : "hidden"} md:flex w-full md:w-80 shrink-0 flex-col border-r border-secondary-200 bg-secondary-100/60`}>
                <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wide text-secondary-500">Show messages from</p>
                <div className="px-3 pb-2">
                  <button type="button" onClick={() => pick(null)} aria-pressed={!selected} className={rowClass(!selected)}>
                    <span className="flex items-center gap-2">
                      <Inbox className={`w-4 h-4 ${!selected ? "text-primary-700" : "text-secondary-500"}`} />
                      <span className={`flex-1 text-sm ${!selected ? "font-bold text-primary-900" : "font-semibold text-secondary-900"}`}>All sessions</span>
                      <span className="text-xs text-secondary-500">{total}</span>
                      {!selected && <Check className="w-4 h-4 text-primary-600" />}
                    </span>
                  </button>
                </div>
                <p className="px-4 pt-1 pb-1 text-[11px] font-bold uppercase tracking-wide text-secondary-500">Or one session</p>
                <ul className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
                  {sessions.map((s) => {
                    const on = selected === s.appointment_id;
                    return (
                      <li key={s.appointment_id}>
                        <button type="button" onClick={() => pick(s.appointment_id)} aria-pressed={on} className={rowClass(on)}>
                          <span className="flex items-center gap-2">
                            <span className={`flex-1 text-sm ${on ? "font-bold text-primary-900" : "font-semibold text-secondary-900"}`}>{sessionTitle(s)}</span>
                            {s.sos_count > 0 && (
                              <span title={`${s.sos_count} SOS request${s.sos_count === 1 ? "" : "s"}`}
                                className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-extrabold text-white ${s.sos_open ? "bg-red-600 animate-pulse" : "bg-red-500"}`}>
                                <Siren className="w-3 h-3" /> SOS{s.sos_count > 1 ? ` ×${s.sos_count}` : ""}
                              </span>
                            )}
                            {s.unread > 0 && <span className="rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">{s.unread}</span>}
                            {on && <Check className="w-4 h-4 text-primary-600" />}
                          </span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-secondary-500">
                            {s.time && <span>{s.time}</span>}
                            {s.clinician && <span>· {s.clinician}</span>}
                            <span>· {s.message_count} {s.message_count === 1 ? "message" : "messages"}</span>
                          </span>
                          <span className="mt-1 flex flex-wrap gap-1">
                            {s.is_current && <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-800">New messages go here</span>}
                            {STATE[s.state] && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATE[s.state].style}`}>{STATE[s.state].label}</span>}
                            {!s.consultation_id && s.state === "past" && <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold text-secondary-600">No notes</span>}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                  {sessions.length === 0 && <li className="px-1 py-6 text-sm text-secondary-400">No sessions yet.</li>}
                </ul>
              </aside>

              {/* Chat */}
              <section className={`${showList ? "hidden" : "flex"} md:flex flex-1 min-w-0 flex-col`}>
                <div className={`flex items-center gap-2 border-b px-4 py-2.5 ${selected ? "border-primary-200 bg-primary-50" : "border-secondary-100"}`}>
                  <button type="button" onClick={() => setShowList(true)} aria-label="Back to sessions" className="md:hidden p-1 rounded-full hover:bg-secondary-100"><ChevronLeft className="w-5 h-5" /></button>
                  <p className="flex-1 min-w-0 text-sm text-secondary-800">
                    {selected
                      ? <>Showing only <span className="font-bold text-primary-900">{sessionTitle(byAppt[selected] || {})}</span></>
                      : <span className="font-semibold">All sessions</span>}
                  </p>
                  {selected && byAppt[selected]?.consultation_id && (
                    <button type="button" onClick={() => navigate(`/consultation/${byAppt[selected].consultation_id}`)} className="text-xs font-semibold text-primary-600 hover:underline">Open notes</button>
                  )}
                  {selected && (
                    <button type="button" onClick={() => setSelected(null)} className="inline-flex items-center gap-1 rounded-full border border-primary-300 bg-white px-2.5 py-1 text-xs font-semibold text-primary-700 hover:bg-primary-100">
                      Show all <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto bg-secondary-50/40 px-4 py-3 space-y-2">
                  {timeline.length === 0 && <p className="py-10 text-center text-sm text-secondary-400">No messages {selected ? "in this session" : "yet"}.</p>}
                  {timeline.map((x, i) => {
                    const prev = timeline[i - 1];
                    const newSession = !selected && (!prev || prev.appointment_id !== x.appointment_id);
                    const s = byAppt[x.appointment_id];
                    const divider = newSession && s && (
                      <div className="my-3 flex items-center gap-2 text-[11px] font-semibold text-secondary-500">
                        <span className="h-px flex-1 bg-secondary-200" />
                        <button type="button" onClick={() => pick(x.appointment_id)} className="rounded-full bg-white border border-secondary-200 px-2.5 py-0.5 hover:border-primary-300">{sessionTitle(s)}</button>
                        <span className="h-px flex-1 bg-secondary-200" />
                      </div>
                    );
                    if (x.kind === "sos") return <div key={`sos-${x.a.id}`}>{divider}<SosCard a={x.a} onOpenRecord={openRecord} /></div>;
                    const m = x.m;
                    const mine = m.sender_type === "doctor";
                    return (
                      <div key={m.id}>
                        {divider}
                        <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${mine ? "bg-primary-600 text-white rounded-br-sm" : "bg-white border border-secondary-100 text-secondary-800 rounded-bl-sm"}`}>
                            <p className={`text-[10px] font-bold mb-0.5 ${mine ? "text-primary-100" : "text-amber-700"}`}>{mine ? (m.sender_name || "Clinic") : (m.sender_name || "Patient")}</p>
                            <p className="whitespace-pre-wrap break-words">{m.body}</p>
                            <p className={`mt-1 text-[10px] ${mine ? "text-primary-100" : "text-secondary-400"}`}>{fmtTime(m.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>

                <div className="border-t border-secondary-100 p-3">
                  {current ? (
                    <>
                      <p className="mb-1.5 text-[11px] text-secondary-500">
                        Saved with <span className="font-semibold text-secondary-700">{sessionTitle(current)}</span>
                        {selected && selected !== current.appointment_id ? ", the current session, not the one you're reading." : " (the current session)."}
                      </p>
                      <div className="flex items-end gap-2">
                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Type a message…" aria-label="Message"
                          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                          className="flex-1 resize-none rounded-xl border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500" />
                        <button type="button" onClick={send} disabled={!draft.trim() || sending} aria-label="Send"
                          className="rounded-xl bg-primary-600 p-3 text-white hover:bg-primary-700 disabled:bg-secondary-300"><Send className="w-4 h-4" /></button>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-secondary-500">This patient has no session yet. Book one to message them; messages are kept with a session.</p>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </Portal>
  );
}
